import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { formatApiError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, IndianRupee, CheckCircle2, FileDown, MailPlus } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtINR } from "@/lib/format";
import { API_BASE } from "@/api/client";

const STATUS = ["pending", "paid", "overdue", "cancelled"];
const STATUS_STYLE = {
    pending: "bg-amber-50 text-amber-700 border-amber-100",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-100",
    overdue: "bg-rose-50 text-rose-700 border-rose-100",
    cancelled: "bg-slate-50 text-slate-500 border-slate-200",
};

const EMPTY_LINE = { description: "", hsn_sac: "", quantity: 1, unit_price: 0, discount_pct: 0, cgst_pct: 9, sgst_pct: 9, igst_pct: 0 };
const EMPTY = {
    client_id: "",
    invoice_no: "",
    description: "",
    notes: "",
    due_date: "",
    status: "pending",
    line_items: [],
};

function computeTotals(items) {
    let subtotal = 0, discount = 0, cgst = 0, sgst = 0, igst = 0;
    for (const li of items || []) {
        const qty = Number(li.quantity) || 0;
        const price = Number(li.unit_price) || 0;
        const gross = qty * price;
        const disc = gross * (Number(li.discount_pct) || 0) / 100;
        const taxable = gross - disc;
        subtotal += gross;
        discount += disc;
        cgst += taxable * (Number(li.cgst_pct) || 0) / 100;
        sgst += taxable * (Number(li.sgst_pct) || 0) / 100;
        igst += taxable * (Number(li.igst_pct) || 0) / 100;
    }
    const taxable_value = subtotal - discount;
    return { subtotal, discount, taxable_value, cgst, sgst, igst, grand_total: taxable_value + cgst + sgst + igst };
}

export default function Payments() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);

    const load = () => api.get("/payments").then((r) => setItems(r.data));

    useEffect(() => {
        load();
        api.get("/clients").then((r) => setClients(r.data.items));
    }, []);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

    const save = async () => {
        try {
            await api.post("/payments", { ...form, amount: Number(form.amount) });
            toast.success("Invoice added");
            setOpen(false);
            setForm(EMPTY);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const markPaid = async (id) => {
        try {
            await api.patch(`/payments/${id}`, { status: "paid", paid_on: new Date().toISOString().slice(0, 10) });
            load();
            toast.success("Marked as paid");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const downloadPdf = async (p) => {
        try {
            const token = localStorage.getItem("ca_access_token");
            const r = await fetch(`${API_BASE}/payments/${p.id}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error("Failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `invoice_${p.invoice_no || p.id.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("PDF download failed");
        }
    };

    const sendReminder = async (p) => {
        try {
            const { data } = await api.post(`/payments/${p.id}/send-reminder`);
            if (data.status === "sent") toast.success("Reminder emailed");
            else if (data.status === "pending")
                toast.success("Reminder queued (SMTP not configured yet)");
            else toast.error("Failed to send reminder");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const totals = items.reduce(
        (acc, p) => {
            if (p.status === "paid") acc.paid += p.amount;
            else if (p.status === "pending" || p.status === "overdue") acc.pending += p.amount;
            return acc;
        },
        { paid: 0, pending: 0 },
    );

    return (
        <div className="space-y-6" data-testid="payments-page">
            <PageHeader
                title="Payments"
                subtitle="Invoices &amp; payment reminders"
                actions={
                    <Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-primary hover:bg-primary/90" data-testid="new-payment-btn">
                        <Plus size={16} className="mr-1.5" /> New invoice
                    </Button>
                }
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="p-5">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Collected</div>
                    <div className="kpi-value text-3xl mt-2 text-emerald-600">{fmtINR(totals.paid)}</div>
                </Card>
                <Card className="p-5">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pending</div>
                    <div className="kpi-value text-3xl mt-2 text-amber-600">{fmtINR(totals.pending)}</div>
                </Card>
                <Card className="p-5">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Invoices</div>
                    <div className="kpi-value text-3xl mt-2 text-slate-800">{items.length}</div>
                </Card>
            </div>

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Invoice #</th>
                                <th className="text-left px-4 py-3 font-semibold">Client</th>
                                <th className="text-left px-4 py-3 font-semibold">Amount</th>
                                <th className="text-left px-4 py-3 font-semibold">Due</th>
                                <th className="text-left px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`payment-row-${p.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.invoice_no || "—"}</td>
                                    <td className="px-4 py-3 text-slate-800">{clientName(p.client_id)}</td>
                                    <td className="px-4 py-3 font-semibold text-slate-800">{fmtINR(p.amount)}</td>
                                    <td className="px-4 py-3 text-slate-600">{fmtDate(p.due_date)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`badge-status ${STATUS_STYLE[p.status]}`}>{p.status}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button size="icon" variant="ghost" onClick={() => downloadPdf(p)} title="Download PDF" data-testid={`pdf-${p.id}`}>
                                                <FileDown size={14} />
                                            </Button>
                                            <Button size="icon" variant="ghost" onClick={() => sendReminder(p)} title="Send reminder" data-testid={`remind-${p.id}`}>
                                                <MailPlus size={14} />
                                            </Button>
                                            {p.status !== "paid" && (
                                                <Button size="sm" variant="outline" onClick={() => markPaid(p.id)} data-testid={`mark-paid-${p.id}`}>
                                                    <CheckCircle2 size={14} className="mr-1" /> Mark paid
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center py-12 text-slate-500">No invoices yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display">New invoice</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <F label="Client">
                            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                                <SelectTrigger data-testid="pay-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Invoice #">
                            <Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="pay-invoice-no" />
                        </F>
                        <F label="Due date">
                            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="pay-due" />
                        </F>
                        <F label="Status">
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {STATUS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </F>
                    </div>

                    {/* Line items editor */}
                    <div className="mt-2">
                        <div className="flex items-center justify-between mb-2">
                            <div className="field-label">Line items</div>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                    setForm((f) => ({
                                        ...f,
                                        line_items: [...(f.line_items || []), { ...EMPTY_LINE }],
                                    }))
                                }
                                data-testid="add-line"
                            >
                                + Add line
                            </Button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs border border-slate-200 rounded-md">
                                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-2 py-2">Description</th>
                                        <th className="text-left px-2 py-2 w-24">HSN/SAC</th>
                                        <th className="text-right px-2 py-2 w-16">Qty</th>
                                        <th className="text-right px-2 py-2 w-24">Unit ₹</th>
                                        <th className="text-right px-2 py-2 w-20">Disc %</th>
                                        <th className="text-right px-2 py-2 w-20">CGST %</th>
                                        <th className="text-right px-2 py-2 w-20">SGST %</th>
                                        <th className="text-right px-2 py-2 w-20">IGST %</th>
                                        <th className="w-8"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(form.line_items || []).map((li, i) => (
                                        <tr key={i} className="border-t border-slate-100">
                                            {[
                                                ["description", "text"],
                                                ["hsn_sac", "text"],
                                                ["quantity", "number"],
                                                ["unit_price", "number"],
                                                ["discount_pct", "number"],
                                                ["cgst_pct", "number"],
                                                ["sgst_pct", "number"],
                                                ["igst_pct", "number"],
                                            ].map(([k, type]) => (
                                                <td key={k} className="px-1 py-1">
                                                    <Input
                                                        type={type}
                                                        value={li[k] ?? ""}
                                                        onChange={(e) => {
                                                            const arr = [...form.line_items];
                                                            arr[i] = {
                                                                ...arr[i],
                                                                [k]:
                                                                    type === "number"
                                                                        ? e.target.value === ""
                                                                            ? ""
                                                                            : Number(e.target.value)
                                                                        : e.target.value,
                                                            };
                                                            setForm({ ...form, line_items: arr });
                                                        }}
                                                        className="h-8 text-xs"
                                                        data-testid={`line-${i}-${k}`}
                                                    />
                                                </td>
                                            ))}
                                            <td className="px-1">
                                                <button
                                                    type="button"
                                                    className="text-rose-600 hover:text-rose-800 text-xs"
                                                    onClick={() =>
                                                        setForm((f) => ({
                                                            ...f,
                                                            line_items: f.line_items.filter((_, idx) => idx !== i),
                                                        }))
                                                    }
                                                >
                                                    ×
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {(form.line_items || []).length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="text-center py-4 text-slate-400 text-xs">
                                                No line items yet — click “Add line”.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {(form.line_items || []).length > 0 && (
                            <div className="mt-3 flex justify-end">
                                <div className="w-full sm:w-80 text-sm space-y-1">
                                    {(() => {
                                        const t = computeTotals(form.line_items);
                                        return (
                                            <>
                                                <Row label="Subtotal" value={t.subtotal} />
                                                {t.discount > 0 && <Row label="Discount" value={-t.discount} />}
                                                <Row label="Taxable value" value={t.taxable_value} />
                                                {t.cgst > 0 && <Row label="CGST" value={t.cgst} />}
                                                {t.sgst > 0 && <Row label="SGST" value={t.sgst} />}
                                                {t.igst > 0 && <Row label="IGST" value={t.igst} />}
                                                <div className="flex justify-between border-t border-slate-200 pt-2 mt-1 font-display font-bold text-slate-900">
                                                    <span>Grand Total</span>
                                                    <span data-testid="grand-total">{fmtINR(t.grand_total)}</span>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                    <F label="Description (short summary)">
                        <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </F>
                    <F label="Notes (shown on the PDF)">
                        <textarea
                            className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            rows={3}
                            value={form.notes || ""}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            data-testid="pay-notes"
                        />
                    </F>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-payment">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Row = ({ label, value }) => (
    <div className="flex justify-between text-slate-600">
        <span>{label}</span>
        <span className="font-mono">{fmtINR(value)}</span>
    </div>
);

const F = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="field-label">{label}</Label>
        {children}
    </div>
);
